import { sendAuthCookiesToAssistExtension } from './assistAuthUtils';
import { withBase } from './basePath';
import { Routes } from './enums';

/**
 * Auth glue for the Zendesk build.
 *
 * In the Zendesk build there is no Cognito session — auth is carried entirely
 * by the Zendesk SSO cookies the upstream proxy injects. The dashboard
 * therefore has these responsibilities:
 *
 *   1. Don't send a bearer token. Every HTTP path consults
 *      `buildAuthHeaders()` (or branches on IS_ZENDESK_BUILD locally) to keep
 *      the `Authorization` header off the wire.
 *   2. On 401, send the user to the build's sign-in page with the current
 *      full URL as `return_to` so they land back here after auth. On 403 send
 *      them to the tenant origin root, and on a suspended-account 401 to the
 *      trial-expired page — in both cases sign-in would just bounce them back
 *      here. Every fetch site funnels through `maybeHandleZendeskAuthFailure`.
 *
 */

let redirecting = false;

// A 401 with this message means the user's account is suspended at the
// tenant. The sign-in page would just bounce them back here, so we route
// them to the tenant origin root instead.
const SUSPENDED_MESSAGE = 'account_suspended';

// Sign-in lives at the origin root (NOT under BASE_PATH) — the proxy
// routes /auth/v3/* to the auth service, while the dashboard SPA is
// mounted under /forethought/dashboard.
function buildLoginUrl(): string {
  const returnTo = encodeURIComponent(window.location.href);
  return `/auth/v3/signin?return_to=${returnTo}`;
}

/**
 * Force a redirect to the Zendesk-build sign-in page. Idempotent across
 * concurrent failing requests — only the first call navigates; subsequent
 * calls are absorbed until the page unloads.
 *
 * Caller is responsible for gating on IS_ZENDESK_BUILD; prefer
 * `maybeHandleZendeskAuthFailure` for response-status-driven dispatch.
 */
export function redirectToZendeskLogin(): void {
  if (redirecting) {
    return;
  }
  redirecting = true;
  window.location.assign(buildLoginUrl());
}

/**
 * Suspended-account fallback: leave the dashboard mount and land at the
 * tenant origin root. The sign-in page would just round-trip the user
 * back here, so bypass it entirely.
 */
export function redirectToZendeskOrigin(): void {
  if (redirecting) {
    return;
  }
  redirecting = true;
  window.location.assign(`${window.location.origin}/`);
}

/**
 * Send a user whose Zendesk-provisioned org is linked to a Forethought org to
 * that org's Forethought-build dashboard. Returns true when a navigation is
 * in flight (or already was), false when the target is unusable and the
 * caller should carry on with the Zendesk build.
 */
export function redirectToForethoughtDashboard(target: string): boolean {
  if (redirecting) {
    return true;
  }
  let url: URL;
  try {
    url = new URL(target);
  } catch {
    return false;
  }
  // Same-origin targets would reload this build in a loop.
  if (
    (url.protocol !== 'https:' && url.protocol !== 'http:') ||
    url.origin === window.location.origin
  ) {
    return false;
  }
  redirecting = true;
  window.location.assign(url.toString());
  return true;
}

/**
 * Suspended-account destination: the standalone trial-expired page. Sign-in
 * would just bounce a suspended session back here, so we route to a dashboard
 * route that explains the state and links to the Admin Center.
 *
 * Uses `withBase` because this is a full navigation (window.location), which
 * bypasses the router basename and therefore needs the BASE_PATH prefix.
 * `initApp` short-circuits the /users/me probe on this path so the suspended
 * 401 that sent the user here doesn't re-fire and loop.
 */
export function redirectToTrialExpired(): void {
  if (redirecting) {
    return;
  }
  redirecting = true;
  window.location.assign(withBase(Routes.TRIAL_EXPIRED));
}

/**
 * Single entry point every HTTP-response path should call after receiving a
 * status. In the Zendesk build, 401/403 triggers a redirect and returns true;
 * otherwise this is a no-op returning false.
 *
 * A 403 routes to the tenant origin root (sign-in would just bounce the user
 * back here). A 401 routes to the sign-in page — except when its JSON body is
 * `{"message":"account_suspended"}`, which routes to the trial-expired page.
 * Callers that have the response message should pass it as the second
 * argument; otherwise prefer `maybeHandleZendeskAuthFailureFromResponse(res)`,
 * which reads the body.
 *
 * Returning a boolean lets callers skip downstream error processing once a
 * redirect is in flight, but ignoring the return value is also safe — the
 * page navigation will tear down in-flight work shortly.
 */
export function maybeHandleZendeskAuthFailure(
  status: number,
  message?: string,
): boolean {
  if (!IS_ZENDESK_BUILD) {
    return false;
  }
  if (status !== 401 && status !== 403) {
    return false;
  }
  if (!redirecting) {
    void sendAuthCookiesToAssistExtension({});
  }
  if (status === 401 && message === SUSPENDED_MESSAGE) {
    // Suspended trial — land on the trial-expired page that links to the
    // Admin Center. Sign-in would just bounce a suspended session back here.
    redirectToTrialExpired();
  } else if (status === 403) {
    // The session is valid but lacks access to this tenant/resource; the
    // sign-in page would just round-trip the user back here, so route them to
    // the tenant origin root instead.
    redirectToZendeskOrigin();
  } else {
    redirectToZendeskLogin();
  }
  return true;
}

/**
 * Response-aware variant of `maybeHandleZendeskAuthFailure`. On 401, clones
 * the response and peeks at the JSON body so the suspended-account branch
 * can fire. The clone keeps the original body stream consumable downstream.
 */
export async function maybeHandleZendeskAuthFailureFromResponse(
  res: Response,
): Promise<boolean> {
  if (!IS_ZENDESK_BUILD) {
    return false;
  }
  if (res.status !== 401 && res.status !== 403) {
    return false;
  }
  let message: string | undefined;
  if (res.status === 401) {
    try {
      const body = await res.clone().json();
      if (typeof body?.message === 'string') {
        message = body.message;
      }
    } catch {}
  }
  return maybeHandleZendeskAuthFailure(res.status, message);
}
